import React, { useState, useEffect } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, Activity, DollarSign, Users, Target } from 'lucide-react';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import ErrorMessage from '../../../../components/ErrorMessage';
import { getCompleteDashboardMetrics } from '../../../../services/company/companyDataService';
import { calculateBasicMetrics } from '../../../../services/metrics/basicMetricsService';
// Dentro de CompanyDashboardCharts (asegura el import de tokens)
import designTokens from '../../../../styles/tokens';
import { useSupabaseClient } from '../../../../lib/supabase';
import CertificatesMetricsCards from './CertificatesMetricsCards';

const MODERN_COLORS = {
  primary: '#3b82f6',
  secondary: '#158B5C',
  accent: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  gradient: ['#3b82f6', '#1d4ed8', '#1e40af']
};

function CompanyDashboardCharts({ companyId, currentLevel, currentData, isLoading, error }) {
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);

  const certificatesData = chartData?.certificates ?? chartData?.certificados ?? [];

  useEffect(() => {
    if (companyId) {
      loadChartData();
    }
  }, [companyId, currentLevel]);

  const supabase = useSupabaseClient();

  const loadChartData = async () => {
    setChartLoading(true);
    setChartError(null);
    
    try {
      const results = await Promise.all([
        getCompleteDashboardMetrics(companyId),
        calculateBasicMetrics(companyId)
      ]);
      if (!Array.isArray(results) || results.length < 2) {
        throw new Error('Error al obtener métricas de la base de datos');
      }
      const [dashboardMetrics, basicMetrics] = results;

      // Rango de fechas usado por la métrica histórica
      const startDate = dashboardMetrics?.historical?.dateRange?.start;
      const endDate = dashboardMetrics?.historical?.dateRange?.end;

      // Históricos reales de cotizaciones y contrataciones (por mes)
      const [cotRes, conRes, sesRes, susRes, cerRes, chRes] = await Promise.all([
        supabase
          .from('company_cotizaciones')
          .select('created_at,resumen,sesion_id,compania_id')
          .eq('compania_id', companyId)
          .gte('created_at', startDate)
          .lte('created_at', endDate),
        supabase
          .from('company_contrataciones')
          .select('created_at')
          .eq('compania_id', companyId)
          .gte('created_at', startDate)
          .lte('created_at', endDate),
        supabase
          .from('company_sesiones')
          .select('id,compania_id,created_at')
          .eq('compania_id', companyId),
        supabase
          .from('company_suscripciones')
          .select('id,company_id,created_at,status,amount,currency,contratacion_id,certificado_id')
          .eq('company_id', companyId),
        supabase
          .from('company_certificados')
          .select('id,compania_id,created_at,fecha_emision,fecha_inicio_cobertura,fecha_termino_cobertura,tipo_certificado,subscription_status,monto,estado_pago,contratacion_id')
          .eq('compania_id', companyId),
        supabase
          .from('company_subscription_charges')
          .select('company_id,compania_nombre,created_at,charge_date,status,amount,currency')
          .eq('company_id', companyId)
      ]);

      const cotizacionesHist = Array.isArray(cotRes?.data) ? cotRes.data : [];
      const contratacionesHist = Array.isArray(conRes?.data) ? conRes.data : [];
      const paymentsHist = dashboardMetrics?.historical?.payments || [];

      const getMonthKey = (iso) => {
        const d = new Date(iso);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleString('es-CL', { month: 'short' });
        return { key, label };
      };

      const monthMap = new Map();
      // Cotizaciones
      cotizacionesHist.forEach((r) => {
        const { key, label } = getMonthKey(r.created_at);
        const cur = monthMap.get(key) || { mes: label, cotizaciones: 0, contrataciones: 0, prima_total: 0 };
        cur.cotizaciones += 1;
        monthMap.set(key, cur);
      });
      // Contrataciones
      contratacionesHist.forEach((r) => {
        const { key, label } = getMonthKey(r.created_at);
        const cur = monthMap.get(key) || { mes: label, cotizaciones: 0, contrataciones: 0, prima_total: 0 };
        cur.contrataciones += 1;
        monthMap.set(key, cur);
      });
      // Pagos (prima mensual)
      paymentsHist.forEach((p) => {
        const { key, label } = getMonthKey(p.created_at);
        const cur = monthMap.get(key) || { mes: label, cotizaciones: 0, contrataciones: 0, prima_total: 0 };
        const isSuccessful = ['paid', 'success', 'completed'].includes((p.status || '').toLowerCase());
        cur.prima_total += isSuccessful ? (p.amount || 0) : 0;
        monthMap.set(key, cur);
      });

      const tendenciasFull = Array.from(monthMap.entries())
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([, v]) => ({
          ...v,
          conversion_rate: v.cotizaciones > 0 ? Math.round((v.contrataciones / v.cotizaciones) * 100) : 0,
        }));
      const tendencias = tendenciasFull.slice(-6);

      const sesCompany = Array.isArray(sesRes?.data) ? sesRes.data : [];
      const cotCompany = Array.isArray(cotRes?.data) ? cotRes.data : [];
      const conCompany = Array.isArray(conRes?.data) ? conRes.data : [];
      const susCompany = Array.isArray(susRes?.data) ? susRes.data : [];
      const cerCompany = Array.isArray(cerRes?.data) ? cerRes.data : [];
      const chargesCompany = Array.isArray(chRes?.data) ? chRes.data : [];

      const mrrUF = cotCompany.reduce((sum, r) => {
        try {
          const resumen = typeof r.resumen === 'string' ? JSON.parse(r.resumen) : r.resumen;
          const primaUF = resumen?.total?.primaUF || 0;
          return sum + Number(primaUF || 0);
        } catch {
          return sum;
        }
      }, 0);
      const arrUF = mrrUF * 12;

      const periodDate = endDate ? new Date(endDate) : new Date();
      const periodYm = `${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}`;
      const processedData = {
        productos: [
          { name: 'Productos Activos', value: basicMetrics.productos_activos || 0, color: MODERN_COLORS.primary },
          { name: 'Productos Inactivos', value: (basicMetrics.total_productos || 0) - (basicMetrics.productos_activos || 0), color: MODERN_COLORS.secondary },
        ],
        planes: [
          { name: 'Planes Activos', sesiones: basicMetrics.total_sesiones || 0, cotizaciones: basicMetrics.total_cotizaciones || 0, contrataciones: basicMetrics.total_contrataciones || 0, count: basicMetrics.planes_activos || 0 },
          { name: 'Planes Inactivos', sesiones: 0, cotizaciones: 0, contrataciones: 0, count: (basicMetrics.total_planes || 0) - (basicMetrics.planes_activos || 0) },
        ],
        metricas: {
          planes: {
            total: basicMetrics.total_planes || 0,
            activos: basicMetrics.planes_activos || 0,
            inactivos: (basicMetrics.total_planes || 0) - (basicMetrics.planes_activos || 0),
            variacion: 8,
          },
          cotizaciones: {
            total: basicMetrics.total_cotizaciones || cotCompany.length || 0,
            valor_total: mrrUF,
            variacion: -5,
          },
          contrataciones: {
            total: basicMetrics.total_contrataciones || conCompany.length || 0,
            tasa_conversion: parseFloat(basicMetrics.tasa_cotizacion_contratacion) || (cotCompany.length > 0 ? (conCompany.length / cotCompany.length) * 100 : 0),
            variacion: 15,
          },
          sesiones: {
            total: basicMetrics.total_sesiones || sesCompany.length || 0
          },
          suscripciones: {
            total: susCompany.length || 0
          },
          certificados: {
            total: cerCompany.length || 0
          },
          ingresos: {
            mrrUF,
            arrUF
          }
        },
        tendencias,
        certificados: cerCompany,
        periodYm,
        company_suscripciones: susCompany,
        company_subscription_charges: chargesCompany
      };

      setChartData(processedData);
    } catch (error) {
      console.error('Error loading chart data:', error);
      setChartError('Error al cargar los datos de gráficos');
    } finally {
      setChartLoading(false);
    }
  };

  const processDataForCharts = (data, level) => {
    if (!data || !Array.isArray(data)) {
      return {
        productos: [],
        planes: [],
        cotizaciones: [],
        contrataciones: []
      };
    }

    // Procesar según el nivel actual
    switch (level) {
      case 'productos':
        return {
          productos: data.map(item => ({
            name: item.nombre || 'Sin nombre',
            value: item.total_planes || 0,
            activos: item.planes_activos || 0,
            inactivos: item.planes_inactivos || 0
          })),
          planes: [],
          cotizaciones: [],
          contrataciones: []
        };
      
      case 'planes':
        return {
          productos: [],
          planes: data.map(item => ({
            name: item.nombre || 'Sin nombre',
            value: item.total_cotizaciones || 0,
            cotizaciones: item.total_cotizaciones || 0,
            contrataciones: item.total_contrataciones || 0
          })),
          cotizaciones: [],
          contrataciones: []
        };
      
      default:
        return {
          productos: [],
          planes: [],
          cotizaciones: [],
          contrataciones: []
        };
    }
  };

  const renderModernTrendsChart = () => {
    if (!chartData?.tendencias || chartData.tendencias.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          No hay datos de tendencias disponibles
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Gráfico de Prima Total */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Prima Total Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData.tendencias}>
                <defs>
                  <linearGradient id="primaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={MODERN_COLORS.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={MODERN_COLORS.primary} stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="mes" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Prima Total']}
                />
                <Area 
                  type="monotone" 
                  dataKey="prima_total" 
                  stroke={MODERN_COLORS.primary}
                  strokeWidth={3}
                  fill="url(#primaGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de Conversión */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Target className="h-5 w-5 text-[var(--yool-success)]" />
              Tasa de Conversión
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData.tendencias}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="mes" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [`${value}%`, 'Conversión']}
                />
                <Line 
                  type="monotone" 
                  dataKey="conversion_rate" 
                  stroke={MODERN_COLORS.secondary}
                  strokeWidth={3}
                  dot={{ fill: MODERN_COLORS.secondary, strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8, stroke: MODERN_COLORS.secondary, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico Combinado */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="h-5 w-5 text-purple-600" />
              Cotizaciones vs Contrataciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.tendencias}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="mes" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="cotizaciones" 
                  fill={MODERN_COLORS.accent}
                  radius={[4, 4, 0, 0]}
                  name="Cotizaciones"
                />
                <Bar 
                  dataKey="contrataciones" 
                  fill={MODERN_COLORS.purple}
                  radius={[4, 4, 0, 0]}
                  name="Contrataciones"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderBarChart = (data, title) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          No hay datos disponibles
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="value" fill="#8884d8" />
          {data[0]?.activos !== undefined && (
            <Bar dataKey="activos" fill="#00C49F" />
          )}
          {data[0]?.inactivos !== undefined && (
            <Bar dataKey="inactivos" fill="#FF8042" />
          )}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (chartLoading || isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Cargando gráficos...</div>
        </CardContent>
      </Card>
    );
  }

  if (chartError || error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-red-500">{chartError || error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4">
        <CertificatesMetricsCards
          data={certificatesData}
          options={{
            periodYm: chartData?.periodYm ?? null,
            companyId,
            company_suscripciones: chartData?.company_suscripciones ?? [],
            company_subscription_charges: chartData?.company_subscription_charges ?? []
          }}
          mode="full"
        />
      </div>
      <div className="space-y-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Resumen</TabsTrigger>
            <TabsTrigger value="distribution">Distribución</TabsTrigger>
            <TabsTrigger value="trends">Tendencias</TabsTrigger>
          </TabsList>

        {/* OVERVIEW apilado vertical */}
        <TabsContent value="overview" className="space-y-6">
          <div className="space-y-6">
            {/* Gráfico de Producción Mensual */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  Producción Mensual
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData?.tendencias || []}>
                    <defs>
                      <linearGradient id="produccionGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor={MODERN_COLORS.primary}
                          stopOpacity={designTokens?.charts?.gradients?.primary?.startOpacity || 0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor={MODERN_COLORS.primary}
                          stopOpacity={designTokens?.charts?.gradients?.primary?.endOpacity || 0.05}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={designTokens?.charts?.axisTick || { fontSize: 12, fill: '#6b7280' }} />
                    <YAxis axisLine={false} tickLine={false} tick={designTokens?.charts?.axisTick || { fontSize: 12, fill: '#6b7280' }} />
                    <Tooltip contentStyle={designTokens?.charts?.tooltip || { backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} />
                    <Area type="monotone" dataKey="cotizaciones" stackId="1" stroke={MODERN_COLORS.accent} fill={MODERN_COLORS.accent} fillOpacity={0.6} name="Cotizaciones" />
                    <Area type="monotone" dataKey="contrataciones" stackId="1" stroke={MODERN_COLORS.secondary} fill={MODERN_COLORS.secondary} fillOpacity={0.8} name="Contrataciones" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Gráfico de Prima Contratada (mensual) */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-[var(--yool-success)]" />
                  Prima Contratada (Suscripción Pagada)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData?.tendencias || []}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={designTokens?.charts?.axisTick || { fontSize: 12, fill: '#6b7280' }} />
                    <YAxis axisLine={false} tickLine={false} tick={designTokens?.charts?.axisTick || { fontSize: 12, fill: '#6b7280' }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={designTokens?.charts?.tooltip || { backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Prima Pagada']} />
                    <Bar dataKey="prima_total" fill={MODERN_COLORS.secondary} radius={designTokens?.charts?.bar?.radius || [4, 4, 0, 0]} name="Prima Contratada" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* DISTRIBUTION apilado y reemplazo PieChart por columnas */}
        <TabsContent value="distribution" className="space-y-6">
          <div className="space-y-6">
            {/* Análisis de Conversión Detallado */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-indigo-600" />
                  Análisis de Conversión
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-white/50 rounded-lg">
                      <div className="text-2xl font-bold text-indigo-600">
                        {chartData?.metricas?.cotizaciones?.total || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Cotizaciones</div>
                    </div>
                    <div className="text-center p-4 bg-white/50 rounded-lg">
                      <div className="text-2xl font-bold text-[var(--yool-success)]">
                        {chartData?.metricas?.contrataciones?.total || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Contrataciones</div>
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg">
                    <div className="text-3xl font-bold">
                      {(
                        (chartData?.metricas?.contrataciones?.total || 0) /
                        Math.max(chartData?.metricas?.cotizaciones?.total || 1, 1) * 100
                      ).toFixed(1)}%
                    </div>
                    <div className="text-sm opacity-90">Tasa de Conversión</div>
                    <div className="text-xs opacity-75 mt-1">
                      Cotización → Contratación
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    <strong>Cálculo:</strong> (Contrataciones ÷ Cotizaciones) × 100
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Distribución de Prima Mensual (Columnas) */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-orange-600" />
                  Distribución de Prima Mensual
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData?.tendencias || []}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={designTokens?.charts?.axisTick || { fontSize: 12, fill: '#6b7280' }} />
                    <YAxis axisLine={false} tickLine={false} tick={designTokens?.charts?.axisTick || { fontSize: 12, fill: '#6b7280' }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={designTokens?.charts?.tooltip || { backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Prima']} />
                    <Bar dataKey="prima_total" fill={MODERN_COLORS.primary} radius={designTokens?.charts?.bar?.radius || [4, 4, 0, 0]} name="Prima" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tendencias */}
        <TabsContent value="trends" className="space-y-6">
          {renderModernTrendsChart()}
        </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

// Se elimina el bloque duplicado que redefinía CompanyDashboardCharts al final del archivo.
export default CompanyDashboardCharts;
